/**
 * BudgetEditorAdapter — official integration between Visual Editor and Budget Engine.
 *
 * Rules:
 * - UI actions → Budget Commands (never mutate totals in the Editor)
 * - Engine changes → Bridges (store / events / selection / history / snapshot)
 * - No React, no DnD, no PDF, no API, no DB
 */

import {
  createBudgetEngine,
  type BudgetCommand,
  type BudgetCommandResult,
  type BudgetDocument,
  type BudgetEngine,
  type BudgetLine,
} from '@/engines/budget'
import { quoteEngine } from '@/engines/quote/QuoteEngine'
import type {
  ComparisonProductSource,
  QuoteBlock,
  QuoteDocumentPro,
} from '@/engines/quote/QuoteTypes'
import { commercialBudgetService } from '@/modules/commercial/services/CommercialBudgetService'
import { commercialQuoteService } from '@/modules/commercial/services/CommercialQuoteService'
import type { CommercialItem, CommitCommercialOptions } from '@/modules/commercial/types'

import { BudgetEventsBridge } from './bridges/BudgetEventsBridge'
import { BudgetHistoryBridge } from './bridges/BudgetHistoryBridge'
import { BudgetSelectionBridge } from './bridges/BudgetSelectionBridge'
import { BudgetSnapshotBridge } from './bridges/BudgetSnapshotBridge'
import { BudgetStoreBridge } from './bridges/BudgetStoreBridge'
import {
  budgetDocumentToQuoteDocument,
  quoteBlockToBudgetLine,
  quoteDocumentToBudgetDocument,
} from './mappers/quote-budget-mapper'

export interface EditorViewState {
  /** PRO-only field until Budget Engine gains SetGlobalMargin. */
  globalMarginPercent: number | null
}

export class BudgetEditorAdapter {
  readonly engine: BudgetEngine
  readonly store: BudgetStoreBridge
  readonly snapshot: BudgetSnapshotBridge
  readonly selection: BudgetSelectionBridge
  readonly events: BudgetEventsBridge
  readonly history: BudgetHistoryBridge

  private view: EditorViewState = { globalMarginPercent: null }

  constructor(engine?: BudgetEngine) {
    this.engine = engine ?? createBudgetEngine()
    this.store = new BudgetStoreBridge(this.engine)
    this.snapshot = new BudgetSnapshotBridge(this.engine)
    this.selection = new BudgetSelectionBridge()
    this.events = new BudgetEventsBridge(this.engine)
    this.history = new BudgetHistoryBridge(this.engine)

    this.store.connect()
    this.events.on('*', () => {
      const doc = this.engine.peek()
      if (doc) this.selection.prune(doc.lines.map((l) => l.id))
    })
  }

  /** Ensure an empty budget exists (Editor bootstrap). */
  ensureDocument(title = 'Novo Orçamento'): BudgetCommandResult {
    if (this.engine.peek()) {
      return {
        ok: true,
        document: this.engine.peek()!,
        events: [],
      }
    }
    return this.dispatch({ type: 'CreateBudget', title })
  }

  createDocument(title?: string): QuoteDocumentPro {
    this.view.globalMarginPercent = null
    this.selection.clear()
    this.dispatch({ type: 'CreateBudget', title: title ?? 'Novo Orçamento PRO' })
    return this.getQuoteDocument()
  }

  /** Dispatch any Budget Command — primary Editor → Engine path. */
  dispatch(command: BudgetCommand): BudgetCommandResult {
    return this.engine.dispatch(command)
  }

  getBudgetDocument(): BudgetDocument | null {
    return this.engine.peek()
  }

  getQuoteDocument(): QuoteDocumentPro {
    const budget = this.engine.peek()
    if (!budget) {
      return this.createDocument()
    }
    return budgetDocumentToQuoteDocument(budget, {
      globalMarginPercent: this.view.globalMarginPercent,
    })
  }

  getViewState(): EditorViewState {
    return { ...this.view }
  }

  setTitle(title: string): QuoteDocumentPro {
    this.dispatch({ type: 'SetTitle', title })
    return this.getQuoteDocument()
  }

  setClient(client: {
    id: string | null
    name: string
    document?: string
    email?: string
    phone?: string
  }): QuoteDocumentPro {
    this.dispatch({
      type: 'SetClient',
      client: {
        id: client.id,
        name: client.name,
        document: client.document,
        email: client.email,
        phone: client.phone,
      },
    })
    return this.getQuoteDocument()
  }

  /**
   * Replace Engine document from a PRO quote document (migration / load / margin sync).
   * Uses RestoreSnapshot command — no silent mutation.
   */
  syncFromQuoteDocument(doc: QuoteDocumentPro): QuoteDocumentPro {
    this.view.globalMarginPercent = doc.globalMarginPercent
    const budget = quoteDocumentToBudgetDocument(doc)
    const result = this.snapshot.replaceDocument(budget)
    if (!result.ok) {
      throw new Error(result.error?.message ?? 'Falha ao sincronizar orçamento')
    }
    return this.getQuoteDocument()
  }

  addFromComparison(source: ComparisonProductSource): QuoteDocumentPro {
    if (!source.productId) throw new Error('Produto sem ProductID — origem inválida')
    if (!source.fingerprint) {
      throw new Error('Produto sem Fingerprint — use o Comparison Engine')
    }

    const line: Omit<BudgetLine, 'id' | 'position'> & { id?: string; position?: number } = {
      type: 'product',
      description: source.description,
      quantity: 1,
      unitCost: source.purchasePrice,
      unitPrice: source.salePrice,
      discountPercent: 0,
      discountValue: 0,
      origin: {
        source: 'comparison',
        productId: source.productId,
        fingerprint: source.fingerprint,
        supplierId: source.supplierId,
        supplierName: source.supplierName,
        externalRef: source.comparisonOfferId,
      },
      meta: {
        quoteBlockType: 'product',
        ...(source.category ? { category: source.category } : {}),
      },
    }

    const result = this.dispatch({ type: 'AddLine', line })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao adicionar produto')
    return this.reprojectWithViewRules()
  }

  addService(serviceId: string): QuoteDocumentPro {
    // Catalog lookup stays in Quote Engine (catalog data); money state via Command.
    const draft = quoteEngine.addService(quoteEngine.createDocument(), serviceId)
    const block = draft.blocks[0]
    if (!block) throw new Error(`Serviço não encontrado: ${serviceId}`)

    const result = this.dispatch({
      type: 'AddLine',
      line: {
        ...quoteBlockToBudgetLine(block),
        id: undefined,
        position: undefined,
      },
    })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao adicionar serviço')
    return this.reprojectWithViewRules()
  }

  addStructural(
    type: Parameters<typeof quoteEngine.addStructural>[1],
    description: string,
    productId: string,
  ): QuoteDocumentPro {
    const draft = quoteEngine.addStructural(quoteEngine.createDocument(), type, description, productId)
    const block = draft.blocks[0]
    if (!block) throw new Error('Falha ao criar bloco estrutural')

    const result = this.dispatch({
      type: 'AddLine',
      line: {
        ...quoteBlockToBudgetLine(block),
        id: undefined,
        position: undefined,
      },
    })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao adicionar bloco')
    return this.reprojectWithViewRules()
  }

  updateBlock(blockId: string, patch: Partial<QuoteBlock>): QuoteDocumentPro {
    const budgetPatch: Partial<Omit<BudgetLine, 'id'>> = {}
    if (patch.description !== undefined) budgetPatch.description = patch.description
    if (patch.quantity !== undefined) budgetPatch.quantity = patch.quantity
    if (patch.purchasePrice !== undefined) budgetPatch.unitCost = patch.purchasePrice
    if (patch.salePrice !== undefined) budgetPatch.unitPrice = patch.salePrice
    if (patch.discountPercent !== undefined) budgetPatch.discountPercent = patch.discountPercent
    if (patch.discountValue !== undefined) budgetPatch.discountValue = patch.discountValue
    if (patch.groupId !== undefined || patch.meta !== undefined) {
      const current = this.engine.peek()?.lines.find((l) => l.id === blockId)
      budgetPatch.meta = {
        ...(current?.meta ?? {}),
        ...(patch.meta ?? {}),
        ...(patch.groupId !== undefined ? { groupId: patch.groupId } : {}),
      }
    }

    const result = this.dispatch({ type: 'UpdateLine', lineId: blockId, patch: budgetPatch })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao atualizar bloco')
    return this.reprojectWithViewRules()
  }

  removeBlock(blockId: string): QuoteDocumentPro {
    const result = this.dispatch({ type: 'RemoveLine', lineId: blockId })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao remover bloco')
    this.selection.prune(result.document.lines.map((l) => l.id))
    return this.reprojectWithViewRules()
  }

  duplicateBlock(blockId: string): QuoteDocumentPro {
    const current = this.engine.peek()?.lines.find((l) => l.id === blockId)
    if (!current) return this.getQuoteDocument()

    const { id: _id, position: _pos, ...rest } = current
    const result = this.dispatch({
      type: 'AddLine',
      line: { ...rest },
    })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao duplicar bloco')
    return this.reprojectWithViewRules()
  }

  moveBlock(fromIndex: number, toIndex: number): QuoteDocumentPro {
    const doc = this.engine.peek()
    if (!doc) return this.getQuoteDocument()
    const ids = [...doc.lines].sort((a, b) => a.position - b.position).map((l) => l.id)
    if (fromIndex < 0 || fromIndex >= ids.length) return this.getQuoteDocument()
    const [item] = ids.splice(fromIndex, 1)
    if (!item) return this.getQuoteDocument()
    ids.splice(toIndex, 0, item)
    const result = this.dispatch({ type: 'ReorderLines', lineIds: ids })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao reordenar')
    return this.reprojectWithViewRules()
  }

  setGlobalDiscount(percent: number, value: number): QuoteDocumentPro {
    const result = this.dispatch({
      type: 'SetGlobalDiscount',
      discountPercent: percent,
      discountValue: value,
    })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao aplicar desconto')
    return this.reprojectWithViewRules()
  }

  /**
   * Global margin remains a PRO view concern until Budget Engine owns it.
   * Applied via QuoteEngine.recompute then RestoreSnapshot command.
   */
  setGlobalMargin(percent: number | null): QuoteDocumentPro {
    this.view.globalMarginPercent = percent
    const projected = this.getQuoteDocument()
    const recomputed = quoteEngine.recompute({
      ...projected,
      globalMarginPercent: percent,
    })
    return this.syncFromQuoteDocument(recomputed)
  }

  setStatus(status: QuoteDocumentPro['status']): QuoteDocumentPro {
    const mapped =
      status === 'DRAFT' || status === 'READY' || status === 'SENT' || status === 'APPROVED'
        ? status
        : 'ARCHIVED'
    const result = this.dispatch({ type: 'SetStatus', status: mapped })
    if (!result.ok) throw new Error(result.error?.message ?? 'Falha ao alterar status')
    return this.getQuoteDocument()
  }

  undo(): QuoteDocumentPro {
    const result = this.history.undo()
    if (!result.ok) return this.getQuoteDocument()
    return this.reprojectWithViewRules()
  }

  redo(): QuoteDocumentPro {
    const result = this.history.redo()
    if (!result.ok) return this.getQuoteDocument()
    return this.reprojectWithViewRules()
  }

  exportJSON(): string {
    return this.snapshot.exportJSON()
  }

  importJSON(raw: string): QuoteDocumentPro {
    const result = this.snapshot.importJSON(raw)
    if (!result.ok) throw new Error(result.error?.message ?? 'JSON inválido')
    this.view.globalMarginPercent = null
    return this.getQuoteDocument()
  }

  /**
   * Commit a CommercialItem into the Budget Engine (Sprint 3.0).
   * Business assembly lives in `@/modules/commercial` — Adapter only dispatches Commands.
   */
  addCommercialItem(item: CommercialItem, options?: CommitCommercialOptions): QuoteDocumentPro {
    commercialBudgetService.addItem(this, item, options)
    return this.reprojectWithViewRules()
  }

  applyCommercialTemplate(
    templateId: string,
    options?: CommitCommercialOptions,
  ): QuoteDocumentPro {
    commercialQuoteService.applyTemplate(this, templateId, options)
    return this.reprojectWithViewRules()
  }

  dispose(): void {
    this.store.disconnect()
    this.events.disconnectAll()
  }

  /**
   * When view margin is set, re-apply QuoteEngine margin projection and sync
   * back via RestoreSnapshot so Engine totals stay authoritative.
   */
  private reprojectWithViewRules(): QuoteDocumentPro {
    if (this.view.globalMarginPercent == null) {
      return this.getQuoteDocument()
    }
    const projected = this.getQuoteDocument()
    const recomputed = quoteEngine.recompute(projected)
    return this.syncFromQuoteDocument(recomputed)
  }
}

/** Shared app-level adapter for Editor PRO wiring. */
export const budgetEditorAdapter = new BudgetEditorAdapter()
